/**
 * Every reason a report can carry, in one place.
 *
 * The registry exists so the set is enumerable: `reason` is a tag, and a tag
 * whose values are invented at each call site cannot be documented, filtered
 * from a saved search, or alerted on. Adding a reason means adding a line
 * here, which is also what keeps `.claude/skills/sentry-search` truthful.
 *
 * Values are the tag as it appears in Sentry, so they stay snake_case, stable,
 * and free of ids — renaming one splits its history into a new issue.
 */
export const REASON = {
  address: {
    applyFailed: 'apply_failed',
    discardedNoCoordinates: 'discarded_no_coordinates',
    selectFailed: 'select_failed',
    storeLinkFailed: 'store_link_failed',
    storeLinkNotPersisted: 'store_link_not_persisted',
  },
  analytics: {
    biDeliveryFailed: 'bi_delivery_failed',
    biPlatformMissing: 'bi_platform_missing',
    tiktokIdentityMissing: 'tiktok_identity_missing',
    tiktokTrackFailed: 'tiktok_track_failed',
  },
  api: {
    requestFailed: 'request_failed',
    unparseableError: 'unparseable_error',
  },
  auth: {
    captchaFailed: 'captcha_failed',
    codeRequestFailed: 'code_request_failed',
    kycSessionFailed: 'kyc_session_failed',
    profileNotFound: 'profile_not_found',
    refreshAnswerLost: 'refresh_answer_lost',
    refreshRejected: 'refresh_rejected',
  },
  cart: {
    calculateFailed: 'calculate_failed',
    calculateRejected: 'calculate_rejected',
  },
  checkout: {
    blocked: 'blocked',
    cartChanged: 'cart_changed',
    deliveryPriceChanged: 'delivery_price_changed',
    deliverySlotsChanged: 'delivery_slots_changed',
    paymentMethodChanged: 'payment_method_changed',
    initFailed: 'init_failed',
    initSkipped: 'init_skipped',
    missingEta: 'missing_eta',
    orderButtonBlocked: 'order_button_blocked',
    orderCreated: 'order_created',
    orderCreateFailed: 'order_create_failed',
    orderCreateFailedNonAxios: 'order_create_failed_non_axios',
    priceBelowMinimum: 'price_below_minimum',
    processFailed: 'process_failed',
  },
  chunk: {
    loadFailed: 'load_failed',
  },
  eta: {
    noWarehouse: 'no_warehouse',
    zonesFetchFailed: 'zones_fetch_failed',
  },
  geo: {
    autoGeolocationFailed: 'auto_geolocation_failed',
    geofencingFailed: 'geofencing_failed',
  },
  hydration: {
    recoveredMismatch: 'recovered_mismatch',
  },
  payment: {
    adyenComponentError: 'adyen_component_error',
    adyenInitFailed: 'adyen_init_failed',
    adyenPaymentFailed: 'adyen_payment_failed',
    adyenSubmitFailed: 'adyen_submit_failed',
    applePayException: 'apple_pay_exception',
    applePayFailed: 'apple_pay_failed',
    cancelled: 'cancelled',
    finalizeMissingIntent: 'finalize_missing_intent',
    googlePayException: 'google_pay_exception',
    googlePayFailed: 'google_pay_failed',
    googlePayTimeout: 'google_pay_timeout',
    methodsLoadFailed: 'methods_load_failed',
    midtransFailed: 'midtrans_failed',
    missingIntent: 'missing_intent',
    myfatoorahError: 'myfatoorah_error',
    stalled: 'stalled',
    stripeConfirmError: 'stripe_confirm_error',
    stripeConfirmException: 'stripe_confirm_exception',
    stripeConfirmTimeout: 'stripe_confirm_timeout',
    stripeMissingIntent: 'stripe_missing_intent',
    stripeNotInitialized: 'stripe_not_initialized',
    timedOut: 'timed_out',
    verificationFailed: 'verification_failed',
    verifone3dsFailed: 'verifone_3ds_failed',
    verifoneFailed: 'verifone_failed',
    verifoneUnavailable: 'verifone_unavailable',
    walletRequestApiFailed: 'wallet_request_api_failed',
  },
  ssr: {
    healthCheckError: 'health_check_error',
    healthCheckTimeout: 'health_check_timeout',
    renderFailed: 'render_failed',
    routePreloadFailed: 'route_preload_failed',
    uncaughtException: 'uncaught_exception',
    unhandledRejection: 'unhandled_rejection',
  },
  ui: {
    blockRenderFailed: 'block_render_failed',
    carouselRenderFailed: 'carousel_render_failed',
    hydrationBoundaryFailed: 'hydration_boundary_failed',
    modalRenderFailed: 'modal_render_failed',
    routeRenderFailed: 'route_render_failed',
  },
  rn: {
    emptyDeviceId: 'empty_device_id',
    handshakeUnusable: 'handshake_unusable',
    invalidDeviceId: 'invalid_device_id',
  },
} as const;

type ValueOf<T> = T[keyof T];

/**
 * Union of every registered reason slug.
 *
 * Mapped over the groups rather than indexed through them: `keyof` a union of
 * objects is the intersection of their keys, which for disjoint groups is
 * `never`.
 */
export type Reason = ValueOf<{
  [Group in keyof typeof REASON]: ValueOf<(typeof REASON)[Group]>;
}>;
